import { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api from 'services/api';
import { showAlert, showConfirm } from 'utils/toastBridge';

/**
 * @typedef {Object} useHiGridHookReturn
 * @property {boolean} loading - loading : 조회/저장 중 로딩 상태
 * @property {(value: boolean) => void} setLoading - setLoading : 로딩 상태 수동 설정
 * @property {(params?: { params: Object }) => Promise<void>} handleSearch - handleSearch : API 조회
 * @property {(defaultDataArg?: Object) => Promise<void>} handleAdd - handleAdd : 행 추가 (선택 행 아래에 삽입)
 * @property {(rows: Array) => Promise<void>} handleAddRows - handleAddRows : 행 추가 (여러 행)
 * @property {() => Promise<void>} handleDelete - handleDelete : 선택 행 삭제
 * @property {(rows: Array) => Promise<void>} handleDeleteRows - handleDeleteRows : 선택 행 삭제 (여러 행)
 * @property {(opts?: { addValue?: Object }, onSuccess?: Function, onError?: Function) => Promise<void>} handleSave - handleSave : API 저장
 * @property {() => void} gridClear - gridClear : 그리드 데이터 비우기
 * @property {() => Array} getData - getData : 전체 그리드 데이터 조회
 * @property {(fnOrValue: Array | ((prev: Array) => Array)) => void} setData - setData : 데이터 설정
 * @property {() => number|null} getSelectedRowIndex - getSelectedRowIndex : 선택된 행 인덱스 (0부터, 없으면 null)
 * @property {(idx: number|null) => void} setSelectedRowIndex - setSelectedRowIndex : 특정 행 선택
 * @property {() => Object|null} getSelectedRow - getSelectedRow : 선택된 행 데이터 객체
 * @property {() => string|number|null} getSelectedRowId - getSelectedRowId : 선택된 행의 rowId
 * @property {(rowId: string|number) => void} setSelectedRowById - setSelectedRowById : rowId로 특정 행 선택
 * @property {(rowId: string|number) => Object|null} getRowById - getRowById : rowId로 행 데이터 조회
 * @property {(rowId: string|number, field: string, value: any) => void} setCellValueById - setCellValueById : rowId로 셀 값 설정
 * @property {() => void} clearSelection - clearSelection : 선택 해제
 * @property {(rowIndex: number, field: string) => any} getCellValue - getCellValue : 특정 셀 값 조회
 * @property {(rowIndex: number, field: string, value: any) => void} setCellValue - setCellValue : 셀 값 설정 (mod 플래그 자동 반영 : U)
 * @property {(rowIndex: number, values: Object) => void} setCellValues - setCellValues : 한 행의 여러 셀 값 일괄 설정 (배칭 이슈 방지)
 * @property {() => number} getRowCount - getRowCount : 행 개수
 * @property {(rowIndex: number) => Object|null} getRow - getRow : 특정 행 데이터
 * @property {() => void} focusGrid - focusGrid : 그리드에 포커스 이동
 * @property {(rowIndex: number, field: string) => void} startEditCell - startEditCell : 해당 셀 편집 모드 진입 (editable 컬럼만)
 * @property {() => void} stopEditCell - stopEditCell : 편집 모드 종료
 * @property {() => void} refresh - refresh : 그리드 데이터 재렌더
 * @property {(requiredFieldsOverride?: string[]) => boolean} requiredValid - 필수값 검증 (column meta.required 또는 override 사용)
 * @property {(fileName?: string) => void} handleExcelDownload - 현재 그리드 데이터를 클라이언트에서 엑셀(.xlsx)로 다운로드 (서버 미사용). 컬럼은 gridRef에서 취득.
 *
 * ---- Tree 전용 (treeOptions 있을 때만) ----
 * @property {() => void} [expandAll] - expandAll : 전체 펼치기
 * @property {() => void} [collapseAll] - collapseAll : 전체 접기
 * @property {(expandChildren?: boolean) => void} [expandSelected] - expandSelected : 선택 노드 펼치기 (인자 true면 하위도 펼침)
 * @property {(collapseChildren?: boolean) => void} [collapseSelected] - collapseSelected : 선택 노드 접기 (인자 true면 하위도 접힘)
 * @property {(checked: boolean) => void} [checkAll] - checkAll : 전체 체크(true) / 전체 해제(false). checkboxField 있을 때만
 * @property {() => Array} [getCheckedData] - getCheckedData : 체크된 행 데이터 배열 (flat, checkboxField 기준)
 *
 * ---- Grid Event Handlers (mstGrid.onXXX = fn 형태로 할당) ----
 * @property {Function|undefined} onInitialize - () => void. 그리드 마운트 완료 시
 * @property {Function|undefined} onSelectRow - (rowData, rowIndex) => void. 행 선택 시
 * @property {Function|undefined} onSelectCell - (rowData, cell) => void. 셀 선택 시. cell = { field }
 * @property {Function|undefined} onRowClicked - ({ data, rowIndex }) => void. 행 클릭 시
 * @property {Function|undefined} onCellClicked - ({ data, colDef, rowIndex }) => void. 셀 클릭 시
 * @property {Function|undefined} onBeforeEditCell - (rowData, { field }) => void. 편집 모드 진입 직전
 * @property {Function|undefined} onAfterEditCell - (rowData, { field }) => void. 편집 모드 진입 직후
 * @property {Function|undefined} onBeforeSaveCell - (rowData, { field, value }) => void. 셀 저장 직전
 * @property {Function|undefined} onAfterSaveCell - (rowData, { field, value }) => void. 셀 저장 직후
 * @property {Function|undefined} onGridClicked - ({ data, rowIndex }) => void. 행 클릭 시
 * @property {Function|undefined} onGridCellClicked - (rowData, { field }, rowIndex) => void. 셀 클릭 시
 * @property {Function|undefined} onGridCellDoubleClicked - (rowData, { field }, rowIndex) => void. 셀 더블클릭 시
 * @property {Function|undefined} onAfterDataLoaded - (data: Array) => void. handleSearch 성공 후 데이터 로드 완료 시
 */

/**
 * hiGrid CRUD + Grid Control Hook
 *
 * HiGridComponent와 gridRef로 연동하여 조회/저장/행추가/삭제와
 * 그리드 제어 메서드(getData, setCellValue 등)를 인스턴스처럼 사용할 수 있는 hook입니다.
 *
 * @param {Object} options
 * @param {React.RefObject} options.gridRef -  gridRef : HiGridComponent에 전달하는 ref
 * @param {() => Object} [options.getSearchValues] - 조회 시 추가할 검색 파라미터 반환 함수
 * @param {string} [options.searchApiEndpoint='/common/select'] - searchApiEndpoint : 조회 API 엔드포인트
 * @param {string} [options.saveApiEndpoint='/common/save'] - saveApiEndpoint : 저장 API 엔드포인트
 * @param {Array} [options.selectQuery=[]] - selectQuery : 조회 쿼리 설정
 * @param {Array} [options.saveQuery=[]] - saveQuery : 저장 쿼리 설정
 * @param {Object} [options.defaultRow={}] - defaultRow : 행 추가 시 기본값
 * @param {boolean} [options.autoSearch=false] - autoSearch : 마운트 시 자동 조회 여부
 * @param {(params: Object) => Promise<Array>} [options.customOnSearch] - customOnSearch : 커스텀 조회 함수 (API 대체)
 * @param {(opts: { saveData, addValue, setData, setSelectedRowIndex }) => Promise<void>} [options.customOnSave] - customOnSave : 커스텀 저장 함수
 * @param {(row: Object) => Object} [options.mapRowBeforeSave] - 저장 API 전송 직전 각 행에 대해 호출 (예: 비마스터에게 세션 기관코드 주입)
 * @param {boolean} [options.confirmCheck=false] - confirmCheck : 삭제/저장 시 확인 다이얼로그 표시 여부
 * @param {Object} [options.treeOptions] - 트리 그리드 시 { idKey, parentKey, childrenKey }. 지정 시 조회는 flat→tree 변환, handleAddRoot/handleAddChild/저장 시 tree→flat 사용
 * @param {boolean} [options.enableContextMenu=false] - 우클릭 메뉴(행추가/삭제/복제/잘라내기/바꾸기)
 * @param {string[]} [options.duplicateExcludeKeys] - 행 복제 시 비울 필드 (PK 등)
 * @param {() => Object} [options.getDefaultRow] - 행 추가 시 defaultRow 대신 동적 기본값
 *
 * @returns {useHiGridHookReturn}
 *
 * @example
 * const gridRef = useRef(null);
 * const crud = useHiGridHook({ gridRef, selectQuery: [...], saveQuery: [...] });
 * crud.handleSearch();
 * const row = crud.getSelectedRow();
 */
export default function useHiGridHook({
  gridRef,
  getSearchValues,
  searchApiEndpoint = '/common/select',
  saveApiEndpoint = '/common/save',
  selectQuery = [],
  saveQuery = [],
  defaultRow = {},
  autoSearch = false,
  customOnSearch,
  customOnSave,
  mapRowBeforeSave,
  confirmCheck = false,
  treeOptions = null,
  enableContextMenu = false,
  duplicateExcludeKeys = [],
  getDefaultRow,
}) {
  const [loading, setLoading] = useState(false);
  /** 컬럼별 옵션 오버라이드. setColOption으로 설정. readOnly: boolean | (row) => boolean 등 */
  const [colOptions, setColOptionsState] = useState({});
  const requestSeqRef = useRef(0);
  const gridEventsRef = useRef({});

  const idKey = treeOptions?.idKey ?? 'cKey';
  const parentKey = treeOptions?.parentKey ?? 'upperCKey';
  const childrenKey = treeOptions?.childrenKey ?? 'subRows';
  const isTree = Boolean(treeOptions);

  /** flat → 트리 변환 (부모키 기준) */
  const buildTreeFromFlat = useCallback((rows, idK, parentK, childK) => {
    const norm = (v) => (v === undefined || v === null ? '' : String(v).trim());
    const map = new Map();
    const roots = [];
    for (const r of rows) {
      // 부서코드 등 id가 비어 있으면 신규 행의 _tempId로 트리 노드 식별
      let id = norm(r[idK]);
      if (!id) id = norm(r._tempId);
      if (!id) continue;
      map.set(id, { ...r, [childK]: [] });
    }
    for (const [, node] of map) {
      const pid = norm(node[parentK]);
      const parent = pid ? map.get(pid) : null;
      if (parent) parent[childK].push(node);
      else roots.push(node);
    }
    return roots;
  }, []);

  /** 트리 → flat (저장용, depth-first) */
  const flattenTree = useCallback((nodes, childK) => {
    const out = [];
    function walk(arr) {
      if (!arr) return;
      arr.forEach((n) => {
        const { [childK]: sub, ...rest } = n;
        out.push(rest);
        walk(sub);
      });
    }
    walk(nodes);
    return out;
  }, []);

  /** 부모 노드 매칭: idKey 값 또는 _tempId(부서코드 미입력 신규 행) */
  const treeParentMatches = useCallback((n, parentIdStr, idK) => {
    const pv = n[idK];
    if (pv != null && String(pv).trim() !== '' && String(pv) === parentIdStr) return true;
    if (n._tempId != null && String(n._tempId) === parentIdStr) return true;
    return false;
  }, []);

  /** 트리에서 특정 부모에 자식 추가 (id 비교 시 숫자/문자열 통일) */
  const addChildToTree = useCallback((nodes, parentId, child, idK, childK) => {
    if (parentId === undefined || parentId === null || parentId === '') return [...(nodes || []), child];
    const parentIdStr = String(parentId);
    return (nodes || []).map((n) => {
      const sub = n[childK];
      if (treeParentMatches(n, parentIdStr, idK)) return { ...n, [childK]: [...(sub || []), child] };
      return { ...n, [childK]: addChildToTree(sub, parentId, child, idK, childK) };
    });
  }, [treeParentMatches]);

  /** 노드가 nodeId와 일치하는지 (idKey 또는 _tempId로 비교, getCheckedData 등에서 넘어온 row 지원) */
  const treeNodeMatches = useCallback((n, nodeId, idK) => {
    if (nodeId == null || nodeId === '') return false;
    const nodeIdStr = String(nodeId).trim();
    if (!nodeIdStr) return false;
    const nId = n[idK];
    const nIdStr = nId != null && nId !== '' ? String(nId).trim() : '';
    if (nIdStr === nodeIdStr) return true;
    if (n._tempId != null && (n._tempId === nodeId || String(n._tempId).trim() === nodeIdStr)) return true;
    return false;
  }, []);

  /** 트리에서 특정 노드에 mod 설정 (id 비교는 idKey 또는 _tempId) */
  const setModInTree = useCallback((nodes, nodeId, mod, idK, childK) => {
    if (nodeId === undefined || nodeId === null) return nodes || [];
    return (nodes || []).map((n) => {
      if (treeNodeMatches(n, nodeId, idK)) return { ...n, mod };
      const sub = n[childK];
      if (!sub || !Array.isArray(sub)) return n;
      return { ...n, [childK]: setModInTree(sub, nodeId, mod, idK, childK) };
    });
  }, [treeNodeMatches]);

  /** 트리에서 특정 노드(및 그 하위 전체) 제거 (mod === 'A' 신규 행 삭제 시 사용) */
  const removeNodeFromTree = useCallback((nodes, nodeId, idK, childK) => {
    if (nodeId === undefined || nodeId === null) return nodes || [];
    return (nodes || [])
      .filter((n) => !treeNodeMatches(n, nodeId, idK))
      .map((n) => {
        const sub = n[childK];
        if (!sub || !Array.isArray(sub)) return n;
        return { ...n, [childK]: removeNodeFromTree(sub, nodeId, idK, childK) };
      });
  }, [treeNodeMatches]);

  // HiGridComponent가 ref.gridEventsRef에서 gridEvents 읽도록 연결
  if (gridRef) gridRef.gridEventsRef = gridEventsRef;
  /** 컬럼 옵션 설정. setColOption(field, { readOnly: true }) 또는 setColOption({ cKey: { readOnly: (row) => row?.mod !== 'A' } }) */
  const setColOption = useCallback((fieldOrMap, options) => {
    if (options !== undefined) {
      const field = fieldOrMap;
      setColOptionsState((prev) => ({ ...prev, [field]: { ...prev[field], ...options } }));
    } else {
      const map = fieldOrMap;
      if (map && typeof map === 'object') {
        setColOptionsState((prev) => {
          const next = { ...prev };
          for (const [f, o] of Object.entries(map)) {
            next[f] = { ...next[f], ...o };
          }
          return next;
        });
      }
    }
  }, []);

  const getData = useCallback(() => gridRef?.current?.getData?.() ?? [], [gridRef]);
  const setGridData = useCallback(
    (fnOrValue) => {
      const g = gridRef?.current;
      if (g?.setData) g.setData(typeof fnOrValue === 'function' ? fnOrValue : () => fnOrValue);
    },
    [gridRef]
  );
  const getSelectedRowIndex = useCallback(() => gridRef?.current?.getSelectedRowIndex?.() ?? null, [gridRef]);
  const setSelectedRowIndex = useCallback(
    (idx) => setTimeout(() => gridRef?.current?.setSelectedRowIndex?.(idx), 0),
    [gridRef]
  );

 

  const handleSearch = useCallback(
    async (params = { params: {} }) => {
      requestSeqRef.current += 1;
      const reqSeq = requestSeqRef.current;
      const searchParams = params.params || {};
      setLoading(true);
      try {
        let list = [];
        if (typeof customOnSearch === 'function') {
          list = await customOnSearch(searchParams);
        } else {
          const req = {
            ...(typeof getSearchValues === 'function' ? getSearchValues() : {}),
            ...(typeof searchParams === 'object' ? searchParams : {}),
            querySet: JSON.stringify({ gridList: selectQuery }),
          };
          const response = await api.post(searchApiEndpoint, req);
          const responseData = response.data?.data || response.data || {};
          if (Array.isArray(responseData)) list = responseData;
          else if (Array.isArray(responseData.gridList)) list = responseData.gridList;
        }

        if (reqSeq !== requestSeqRef.current) {
          setLoading(false);
          return;
        }

        let finalData = Array.isArray(list) ? list : [];
        if (isTree && finalData.length > 0) {
          finalData = buildTreeFromFlat(finalData, idKey, parentKey, childrenKey);
        }
        setGridData(finalData);
        gridRef?.current?.clearColumnFilters?.();
        if (finalData.length > 0) setSelectedRowIndex(0);
        else setSelectedRowIndex(null);

        // 그리드가 데이터를 반영한 뒤 실행 (autoSearch 시 setData 비동기 반영 후 expandAll 등 동작 보장)
        const onAfter = gridEventsRef.current.onAfterDataLoaded;
        if (typeof onAfter === 'function') {
          setTimeout(() => onAfter(finalData), 0);
        }
      } catch (e) {
        showAlert(`조회 중 오류: ${e?.message || String(e)}`, 'error');
      } finally {
        setLoading(false);
      }
    },
    [customOnSearch, getSearchValues, searchApiEndpoint, selectQuery, setGridData, setSelectedRowIndex, isTree, buildTreeFromFlat, idKey, parentKey, childrenKey]
  );

  const resolveDataIndex = useCallback((data, row) => {
    if (!row || !Array.isArray(data)) return -1;
    if (row._tempId != null) {
      const byTemp = data.findIndex((r) => r._tempId === row._tempId);
      if (byTemp >= 0) return byTemp;
    }
    if (row.id != null && row.id !== '') {
      const byId = data.findIndex((r) => r.id === row.id);
      if (byId >= 0) return byId;
    }
    // 도메인 불변키 — 참조 비교 실패·확인창 await 후에도 행을 찾을 수 있게
    const uidKeys = [
      'docUid',
      'detailUid',
      'detailId',
      'evidenceUid',
      'fundUid',
      'fundPkgUid',
      'memberId',
      'deptUid',
      'subjectUid',
      'projectUid',
      'roundUid',
      'mapUid',
      'histId',
      'proposalUid',
      'obligationUid',
      'resolutionUid',
      'contractUid',
      'requestUid',
    ];
    for (const key of uidKeys) {
      if (row[key] != null && row[key] !== '') {
        const byUid = data.findIndex((r) => r[key] === row[key]);
        if (byUid >= 0) return byUid;
      }
    }
    const byRef = data.indexOf(row);
    return byRef;
  }, []);

  const buildNewRow = useCallback(
    (defaultDataArg = {}) => {
      const base =
        typeof getDefaultRow === 'function' ? { ...defaultRow, ...getDefaultRow() } : { ...defaultRow };
      const newRow = { ...base, ...defaultDataArg };
      newRow._tempId = `__NEW_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      newRow.mod = 'A';
      return newRow;
    },
    [defaultRow, getDefaultRow]
  );

  const handleAdd = useCallback(
    async (defaultDataArg = {}, opts = {}) => {
      const position = opts?.position === 'above' ? 'above' : 'below';
      try {
        if (isTree) {
          const newRow = buildNewRow({
            ...defaultDataArg,
            [parentKey]: defaultDataArg[parentKey] ?? '',
          });
          setGridData((prev) => [...(prev || []), newRow]);
          setSelectedRowIndex(0);
          return;
        }
        const data = getData();
        const selected = gridRef?.current?.getSelectedRow?.() ?? null;
        let insertIndex = data.length;
        if (selected) {
          const idx = resolveDataIndex(data, selected);
          if (idx >= 0) insertIndex = position === 'above' ? idx : idx + 1;
        } else if (position === 'above') {
          insertIndex = 0;
        }
        const newRow = buildNewRow(defaultDataArg);

        setGridData((prev) => {
          const next = [...(prev || [])];
          const idx = Math.min(Math.max(0, insertIndex), next.length);
          next.splice(idx, 0, newRow);
          return next;
        });
        setSelectedRowIndex(insertIndex);
      } catch (e) {
        showAlert(`행 추가 중 오류: ${e?.message || String(e)}`, 'error');
      }
    },
    [buildNewRow, getData, setGridData, setSelectedRowIndex, isTree, parentKey, resolveDataIndex, gridRef]
  );

  const handleAddAbove = useCallback(
    async (defaultDataArg = {}) => handleAdd(defaultDataArg, { position: 'above' }),
    [handleAdd]
  );

  // array 형태로 행 추가
  const handleAddRows = useCallback(async (rows) => {
    try {
      for (const defaultDataArg of rows || []) {
        await handleAdd(defaultDataArg);
      }
    } catch (e) {
      showAlert(`행 추가 중 오류: ${e?.message || String(e)}`, 'error');
    }
  }, [handleAdd]);

  const handleDuplicate = useCallback(async () => {
    if (isTree) {
      showAlert('트리 그리드에서는 행 복제를 지원하지 않습니다.', 'warning');
      return;
    }
    const selected = gridRef?.current?.getSelectedRow?.();
    if (!selected) {
      showAlert('복제할 행을 선택해주세요.', 'warning');
      return;
    }
    const exclude = new Set([
      'mod',
      '_tempId',
      ...(Array.isArray(duplicateExcludeKeys) ? duplicateExcludeKeys : []),
    ]);
    const copied = {};
    Object.keys(selected).forEach((key) => {
      if (exclude.has(key)) return;
      copied[key] = selected[key];
    });
    exclude.forEach((key) => {
      if (key === 'mod' || key === '_tempId') return;
      if (key in selected) copied[key] = '';
    });
    await handleAdd(copied, { position: 'below' });
  }, [isTree, gridRef, duplicateExcludeKeys, handleAdd]);



  /** 트리 전용: 선택 노드에 자식 추가 */
  const handleAddChild = useCallback(
    async (defaultDataArg = {}) => {
      if (!isTree) return;
      try {
        const selected = gridRef?.current?.getSelectedRow?.();
        if (!selected) {
          showAlert('자식을 추가할 부모 행을 먼저 선택해주세요.', 'warning');
          return;
        }
        const pc = selected[idKey];
        const parentRef =
          pc != null && String(pc).trim() !== '' ? String(pc).trim() : (selected._tempId ?? '');
        const newChild = {
          ...defaultRow,
          ...defaultDataArg,
          [parentKey]: parentRef,
          mod: 'A',
          _tempId: `__NEW_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        };
        setGridData((prev) => addChildToTree(prev || [], parentRef, newChild, idKey, childrenKey));
        // refresh() 호출 제거: setData가 비동기라 refresh()가 아직 반영 안 된 dataRef로 덮어써 추가한 자식이 사라지는 문제 방지
      } catch (e) {
        showAlert(`자식 추가 중 오류: ${e?.message || String(e)}`, 'error');
      }
    },
    [defaultRow, isTree, idKey, parentKey, childrenKey, addChildToTree, gridRef, setGridData]
  );

  const handleDeleteRows = useCallback(async (rows) => {
    if (!rows || rows.length === 0) {
      showAlert('삭제할 항목을 먼저 선택(체크)해주세요.', 'warning');
      return;
    }

    if (confirmCheck) {
      const ok = await showConfirm(
        rows.length > 1 ? `선택한 ${rows.length}건을 삭제하시겠습니까?` : '삭제하시겠습니까?',
        'warning'
      );
      if (!ok) return;
    }

    try {
      if (isTree) {
        setGridData((prev) => {
          let next = prev || [];
          rows.forEach((row) => {
            const nodeId = row[idKey] ?? row._tempId ?? row.id;
            if (nodeId == null || nodeId === '') return;
            if (row.mod === 'A') {
              next = removeNodeFromTree(next, nodeId, idKey, childrenKey);
            } else {
              next = setModInTree(next, nodeId, 'D', idKey, childrenKey);
            }
          });
          return next;
        });
      } else {
        setGridData((prev) => {
          const list = [...(prev || [])];
          const removeIdx = new Set();
          const markIdx = new Set();
          rows.forEach((delRow) => {
            const idx = resolveDataIndex(list, delRow);
            if (idx < 0) return;
            if (list[idx]?.mod === 'A') removeIdx.add(idx);
            else markIdx.add(idx);
          });
          markIdx.forEach((idx) => {
            if (list[idx]) list[idx] = { ...list[idx], mod: 'D' };
          });
          return list.filter((_, i) => !removeIdx.has(i));
        });
      }
      gridRef?.current?.clearRowChecks?.();
    } catch (e) {
      showAlert(`삭제 중 오류: ${e?.message || String(e)}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [setGridData, isTree, idKey, childrenKey, setModInTree, removeNodeFromTree, confirmCheck, resolveDataIndex, gridRef, setLoading]);

  const handleDelete = useCallback(async () => {
    const data = getData();
    if (!data?.length) {
      showAlert('삭제할 행을 선택해주세요.', 'warning');
      return;
    }

    // 행 체크가 있으면 멀티 삭제 우선
    const checked = gridRef?.current?.getCheckedData?.() ?? [];
    if (checked.length > 0) {
      await handleDeleteRows(checked);
      return;
    }

    if (isTree) {
      const row = gridRef?.current?.getSelectedRow?.();
      const nodeId = row?.[idKey];
      if (!row || nodeId == null) {
        showAlert('삭제할 행을 선택하거나 체크해주세요.', 'warning');
        return;
      }
      if (confirmCheck) {
        const ok = await showConfirm('삭제하시겠습니까?', 'warning');
        if (!ok) return;
      }
      try {
        if (row.mod === 'A') {
          setGridData((prev) => removeNodeFromTree(prev || [], nodeId, idKey, childrenKey));
        } else {
          setGridData((prev) => setModInTree(prev || [], nodeId, 'D', idKey, childrenKey));
        }
        setTimeout(() => setSelectedRowIndex(0), 0);
      } catch (e) {
        showAlert(`삭제 중 오류: ${e?.message || String(e)}`, 'error');
      }
      return;
    }

    const row = gridRef?.current?.getSelectedRow?.();
    let selectedRowIndex = resolveDataIndex(data, row);
    if (!row || selectedRowIndex < 0) {
      showAlert('삭제할 행을 선택하거나 체크해주세요.', 'warning');
      return;
    }

    const rowSnap = { ...row };
    const wasAdd = row.mod === 'A';

    if (confirmCheck) {
      const ok = await showConfirm('삭제하시겠습니까?', 'warning');
      if (!ok) return;
    }

    try {
      setGridData((prev) => {
        const list = prev || [];
        let idx = resolveDataIndex(list, rowSnap);
        if (idx < 0) idx = selectedRowIndex;
        if (idx < 0 || idx >= list.length) return list;
        if (wasAdd || list[idx]?.mod === 'A') {
          return list.filter((_, i) => i !== idx);
        }
        const next = [...list];
        next[idx] = { ...next[idx], mod: 'D' };
        return next;
      });
      const latestLen = (getData() || []).length;
      const nextIndex = Math.max(0, Math.min(selectedRowIndex, Math.max(0, latestLen - 1)));
      setSelectedRowIndex(latestLen <= 0 ? null : nextIndex);
    } catch (e) {
      showAlert(`삭제 중 오류: ${e?.message || String(e)}`, 'error');
    }
  }, [getData, setGridData, setSelectedRowIndex, confirmCheck, isTree, idKey, childrenKey, setModInTree, removeNodeFromTree, gridRef, resolveDataIndex, handleDeleteRows]);

  const handleSave = useCallback(
    async ({ addValue = {} } = {}, onSuccess = () => {}, onError = () => {}) => {
      try {
        const isValid = gridRef?.current?.requiredValid?.();
        if (isValid === false) return;
        let currentRows = getData();
        if (isTree && Array.isArray(currentRows) && currentRows.length > 0) {
          currentRows = flattenTree(currentRows, childrenKey);
        }

        // 행 체크가 있으면 체크된 행만 저장 대상
        const checked = gridRef?.current?.getCheckedData?.() ?? [];
        if (checked.length > 0) {
          const picked = [];
          checked.forEach((c) => {
            const idx = resolveDataIndex(currentRows, c);
            if (idx >= 0) picked.push(currentRows[idx]);
          });
          currentRows = picked;
        }

        // 트리: 상위가 아직 부서코드 미입력이면 parentKey에 _tempId가 들어가므로, 저장 직전에 실제 부서코드로 치환
        if (isTree && Array.isArray(currentRows) && currentRows.length > 0) {
          const fullFlat = flattenTree(getData() || [], childrenKey);
          const norm = (v) => (v === undefined || v === null ? '' : String(v).trim());
          const byTemp = new Map();
          fullFlat.forEach((r) => {
            if (r._tempId != null && norm(r._tempId)) byTemp.set(norm(r._tempId), r);
          });
          currentRows = currentRows.map((r) => {
            const p = r[parentKey];
            if (p == null || norm(p) === '') return { ...r };
            const pStr = norm(p);
            if (!byTemp.has(pStr)) return { ...r };
            const parentRow = byTemp.get(pStr);
            const pcode = parentRow[idKey];
            if (pcode == null || norm(pcode) === '') {
              return { ...r, __parentCodeMissing: true };
            }
            return { ...r, [parentKey]: norm(pcode) };
          });
          for (const r of currentRows) {
            if (r.__parentCodeMissing) {
              showAlert('상위 부서의 부서코드를 먼저 입력해주세요.', 'warning');
              return;
            }
            if (r.mod !== 'D' && !norm(r[idKey])) {
              showAlert('부서코드를 입력해주세요.', 'warning');
              return;
            }
          }
          currentRows = currentRows.map(({ __parentCodeMissing, ...rest }) => rest);
        }
        const created = currentRows.filter((r) => r.mod === 'A').map(({ _tempId, ...rest }) => rest);
        const updated = currentRows.filter((r) => r.mod === 'U');
        const deleted = currentRows.filter((r) => r.mod === 'D');
        let saveData = [...created, ...updated, ...deleted];
        if (typeof mapRowBeforeSave === 'function') {
          saveData = saveData.map((r) => mapRowBeforeSave(r));
        }

        if (!saveData.length) {
          showAlert(
            checked.length > 0
              ? '체크한 행 중 저장할 변경분이 없습니다.'
              : '저장할 데이터가 없습니다.',
            'warning'
          );
          return;
        }

        if (confirmCheck) {
          const ok = await showConfirm('저장하시겠습니까?', 'success');
          if (!ok) return;
        }

        if (typeof customOnSave === 'function') {
          await customOnSave({ saveData, addValue, setData: setGridData, setSelectedRowIndex });
          gridRef?.current?.clearRowChecks?.();
          onSuccess?.();
          return;
        }

        const saveLogicObj = {
          logic: [{ actionType: 'list', query: saveQuery, data: saveData }],
        };
        const forwardData = { logicObj: JSON.stringify(saveLogicObj) };
        if (addValue && Object.keys(addValue).length > 0) {
          forwardData.addValue = JSON.stringify(addValue);
        }

        
        const response = await api.post(saveApiEndpoint, forwardData);
        const responseData = response.data?.data || response.data || {};
        if (responseData.Msg?.toLowerCase?.() === 'success') {
          await handleSearch();
          gridRef?.current?.clearRowChecks?.();
          onSuccess?.(responseData);
        } else {
          onError?.(`저장 실패 : ${responseData.Msg || 'unknown'}`);
        }
      } catch (e) {
        onError?.(e?.message || String(e));
      }
    },
    [getData, gridRef, setGridData, setSelectedRowIndex, customOnSave, mapRowBeforeSave, saveApiEndpoint, saveQuery, handleSearch, confirmCheck, isTree, childrenKey, flattenTree, parentKey, idKey, resolveDataIndex]
  );

  const gridClear = useCallback(() => {
    setGridData([]);
    setSelectedRowIndex(null);
    gridRef?.current?.clearRowChecks?.();
  }, [setGridData, setSelectedRowIndex, gridRef]);

  /**
   * 트리 구조를 1차원 배열로 펼침 (엑셀 등 내보내기용)
   */
  const flattenTreeRows = useCallback((rows, childKey) => {
    if (!Array.isArray(rows)) return [];
    const out = [];
    for (const r of rows) {
      const { [childKey]: children, ...rest } = r;
      out.push(rest);
      if (Array.isArray(children) && children.length > 0) {
        out.push(...flattenTreeRows(children, childKey));
      }
    }
    return out;
  }, []);

  /**
   * 현재 그리드 데이터를 클라이언트에서 엑셀(.xlsx)로 다운로드 (서버 미사용)
   * 컬럼은 gridRef.current.getDataColumns()로 취득 (HiGrid에 넘긴 데이터 컬럼과 동일)
   * @param {string} [fileName='grid_export.xlsx'] - 다운로드 파일명
   */
  const handleExcelDownload = useCallback((fileName = 'grid_export') => {
    const cols = Array.isArray(gridRef?.current?.getDataColumns?.()) ? gridRef.current.getDataColumns() : [];
    const dataRows = getData();
    const rowsToExport = isTree ? flattenTreeRows(dataRows, childrenKey) : dataRows;

    if (rowsToExport.length === 0) {
      showAlert('다운로드할 데이터가 없습니다.', 'info');
      return;
    }

    const headers = cols.map((c) => c.header ?? c.accessorKey ?? c.id ?? '');
    const keys = cols.map((c) => c.accessorKey ?? c.id ?? '');
    const aoa = [headers];
    for (const row of rowsToExport) {
      aoa.push(keys.map((k) => (row[k] != null && row[k] !== '' ? row[k] : '')));
    }

    try {
      setLoading(true);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
      setLoading(false);
    } catch (e) {
      showAlert(`엑셀 다운로드 중 오류: ${e?.message || String(e)}`, 'error');
      setLoading(false);
    }
  }, [getData, isTree, childrenKey, flattenTreeRows, setLoading , gridRef]);

  useEffect(() => {
    if (!autoSearch) return;
    handleSearch();
  }, [autoSearch]); // eslint-disable-line react-hooks/exhaustive-deps


  


  return {
    // ---- CRUD 액션 ----
    loading,
    setLoading,
    handleSearch,
    handleAdd,
    handleAddAbove,
    handleAddRows,
    handleDelete,
    handleDeleteRows,
    handleDuplicate,
    handleSave,
    gridClear,
    handleExcelDownload,
    // 트리 전용 (treeOptions 있을 때만 의미 있음)
    handleAddRoot: isTree ? handleAdd : undefined,
    handleAddChild: isTree ? handleAddChild : undefined,

    /** HiGridComponent에 펼쳐 넣을 우클릭 메뉴 props */
    contextMenuProps: enableContextMenu
      ? {
          enableContextMenu: true,
          enableRowEditMenu: !isTree,
          enableCut: true,
          enableFindReplace: true,
          allowReplace: true,
          onContextAddAbove: handleAddAbove,
          onContextAddBelow: handleAdd,
          onContextDelete: handleDelete,
          onContextDuplicate: handleDuplicate,
        }
      : {
          enableContextMenu: false,
        },

    // ---- Grid Control Methods (인스턴스처럼 사용) ----
    getData: () => gridRef?.current?.getData?.() ?? [],
    getDataColumns: () => gridRef?.current?.getDataColumns?.() ?? [],
    setData: (fnOrValue) => gridRef?.current?.setData?.(fnOrValue),
    getSelectedRowIndex: () => gridRef?.current?.getSelectedRowIndex?.() ?? null,
    setSelectedRowIndex: (idx) => setTimeout(() => gridRef?.current?.setSelectedRowIndex?.(idx), 0),
    getSelectedRow: () => gridRef?.current?.getSelectedRow?.() ?? null,
    getSelectedRowId: () => gridRef?.current?.getSelectedRowId?.() ?? null,
    setSelectedRowById: (rowId) => gridRef?.current?.setSelectedRowById?.(rowId),
    getRowById: (rowId) => gridRef?.current?.getRowById?.(rowId) ?? null,
    setCellValueById: (rowId, field, value) => gridRef?.current?.setCellValueById?.(rowId, field, value),
    clearSelection: () => gridRef?.current?.clearSelection?.(),
    getCellValue: (rowIndex, field) => gridRef?.current?.getCellValue?.(rowIndex, field),
    setCellValue: (rowIndex, field, value) => gridRef?.current?.setCellValue?.(rowIndex, field, value),
    /** 한 번에 여러 셀 값 반영 (rowIndex, { field: value, ... }) */
    setCellValues: (rowIndex, values) => gridRef?.current?.setCellValues?.(rowIndex, values),
    getRowCount: () => gridRef?.current?.getRowCount?.() ?? 0,
    getRow: (rowIndex) => gridRef?.current?.getRow?.(rowIndex) ?? null,
    focusGrid: () => gridRef?.current?.focusGrid?.(),
    startEditCell: (rowIndex, field) => gridRef?.current?.startEditCell?.(rowIndex, field),
    stopEditCell: () => gridRef?.current?.stopEditCell?.(),
    refresh: () => gridRef?.current?.refresh?.(),
    requiredValid: (requiredFieldsOverride) => gridRef?.current?.requiredValid?.(requiredFieldsOverride),
    /** 컬럼 옵션 (readOnly 등). setColOption으로 변경 시 그리드에 colOptions prop 전달 필요 */
    colOptions,
    setColOption,

    // ---- Tree 전용 (treeOptions 있을 때만) ----
    expandAll: isTree ? () => gridRef?.current?.expandAll?.() : undefined,
    collapseAll: isTree ? () => gridRef?.current?.collapseAll?.() : undefined,
    expandSelected: isTree ? (expandChildren = true) => gridRef?.current?.expandSelected?.(expandChildren) : undefined,
    collapseSelected: isTree ? (collapseChildren = true) => gridRef?.current?.collapseSelected?.(collapseChildren) : undefined,
    checkAll: (checked) => gridRef?.current?.checkAll?.(checked),
    getCheckedData: () => gridRef?.current?.getCheckedData?.() ?? [],
    clearRowChecks: () => gridRef?.current?.clearRowChecks?.(),

    // ---- Grid Event Handlers (mstGrid.onInitialize = fn 형태로 할당) ----
    get onInitialize() { return gridEventsRef.current.onInitialize; },
    set onInitialize(fn) { gridEventsRef.current.onInitialize = fn; },
    get onSelectRow() { return gridEventsRef.current.onSelectRow; },
    set onSelectRow(fn) { gridEventsRef.current.onSelectRow = fn; },
    get onSelectCell() { return gridEventsRef.current.onSelectCell; },
    set onSelectCell(fn) { gridEventsRef.current.onSelectCell = fn; },
    get onRowClicked() { return gridEventsRef.current.onRowClicked; },
    set onRowClicked(fn) { gridEventsRef.current.onRowClicked = fn; },
    get onCellClicked() { return gridEventsRef.current.onCellClicked; },
    set onCellClicked(fn) { gridEventsRef.current.onCellClicked = fn; },
    get onBeforeEditCell() { return gridEventsRef.current.onBeforeEditCell; },
    set onBeforeEditCell(fn) { gridEventsRef.current.onBeforeEditCell = fn; },
    get onAfterEditCell() { return gridEventsRef.current.onAfterEditCell; },
    set onAfterEditCell(fn) { gridEventsRef.current.onAfterEditCell = fn; },
    get onBeforeSaveCell() { return gridEventsRef.current.onBeforeSaveCell; },
    set onBeforeSaveCell(fn) { gridEventsRef.current.onBeforeSaveCell = fn; },
    get onAfterSaveCell() { return gridEventsRef.current.onAfterSaveCell; },
    set onAfterSaveCell(fn) { gridEventsRef.current.onAfterSaveCell = fn; },
    get onGridClicked() { return gridEventsRef.current.onGridClicked; },
    set onGridClicked(fn) { gridEventsRef.current.onGridClicked = fn; },
    get onGridCellClicked() { return gridEventsRef.current.onGridCellClicked; },
    set onGridCellClicked(fn) { gridEventsRef.current.onGridCellClicked = fn; },
    get onGridCellDoubleClicked() { return gridEventsRef.current.onGridCellDoubleClicked; },
    set onGridCellDoubleClicked(fn) { gridEventsRef.current.onGridCellDoubleClicked = fn; },
    get onAfterDataLoaded() { return gridEventsRef.current.onAfterDataLoaded; },
    set onAfterDataLoaded(fn) { gridEventsRef.current.onAfterDataLoaded = fn; },
  };
}
